// essentials
import { IAC_EVENT_TYPES } from './constants'
// communication
import { Config, IACCommunication, IACEndpoint, IACHub } from '.'
import * as Logger from '@navigator/frontend-logger'

// application
import { BaseApplication, DialogApplication, FullScreenApplication } from './application'

// navigator modules
import {
  Connectivity,
  InterruptionHandler,
  NavigatorError,
  ScriptInterruption,
} from './navigator-services'

// interface
import { KeepAlive, MonitoringLoggingQueue, NavigatorConfig, NavigatorInterface, ToolbarInterface, } from './interface'
import { ToolbarDataEvent } from './interface/ToolbarInterface'
import { timeoutPromise } from '../../../util'

const initializedApps: {
  [key: string]: BaseApplication;
} = {};
let isStartupApplied = false;

const TOOLBAR_STARTUP_TIMEOUT = 300;

export class Navigator {

  public static init(): void {
    this.initApps();

    // initialize additional navigator modules
    KeepAlive.run();
    Connectivity.init();
    ScriptInterruption.run();
    InterruptionHandler.init();
    ToolbarInterface.init();

    /* istanbul ignore next else */
    if (IACCommunication.hasUsecase('add_lps')) {
      const usecase = IACCommunication.createUsecase('add_lps');
      usecase.asRPE();
      IACHub.handle(usecase);
    }

  }

  private static initApps() {
    const apps = Config.get().navigator.apps;

    Object.keys(apps).forEach((sysname: string) => {
      const appConfig = apps[sysname];
      let appObject;

      switch (appConfig.view_mode) {
        case 'application':
          appObject = new FullScreenApplication();
          break;

        case 'dialog':
          appObject = new DialogApplication();
          break;

        default:
          throw new NavigatorError(`Unknown component type '${appConfig.view_mode}'`);
      }

      appObject.config = appConfig;
      initializedApps[sysname] = appObject;
    });
  }

  public static openFeedBack(): void {
    const usecase = this.getApplication('feedback_dialog').getInitialUsecase();

    usecase.setPayload({ env: 'poseidon' });
    usecase.protocol.setSender(this.getFocusedApplication().getSysname());

    IACHub.handle(usecase);
  }

  public static resumeFocusedApp(sysname: string): void {
    const application = this.getFocusedApplication();

    if (application.getSysname() === sysname) {
      IACHub.handle(application.getInitialUsecase());
    }
  }

  public static handleLogout(): void {
    IACHub.handle(IACCommunication.createUsecase('tracking_track', { section: 'click.logout' }).asRPE());
    this.logout();
  }

  public static async handleStartup(): Promise<void> {
    if (this.isStartupApplied()) {
      return;
    }
    isStartupApplied = true;

    const startup = Config.get().navigator.startup;

    if (startup.toolbarevent) {
      // Toolbar events always wins
      const startupResponse = await this.toolbarStartup(TOOLBAR_STARTUP_TIMEOUT);
      if (!startupResponse || !startupResponse.payload?.success) {
        const errorDetail = startupResponse ? startupResponse.payload : 'No ToolbarDataEvent was dispatched';
        Logger.error('Error while handling toolbar use case', errorDetail);
        this.invokeStartupUsecases(startup.usecases);
      }
    } else {
      // Start the default use cases
      this.invokeStartupUsecases(startup.usecases);
    }
  }

  /**
   * Returns a promise that resolves with the result of ToolbarInterface.toolbarStartupPromise or void if
   * toolbarStartupPromise does not resolve within the given timeout.
   */
  public static toolbarStartup(timeout: number): Promise<ToolbarDataEvent['detail']|void> {
    return Promise.race([
      timeoutPromise(timeout),
      ToolbarInterface.toolbarStartupPromise()
    ])
  }

  /**
   * Invokes the given startup use cases
   * @param usecases
   */
  public static invokeStartupUsecases(usecases: NavigatorConfig['startup']['usecases']): void {
    Object.keys(usecases).forEach((usecaseName) => {
      // start application
      IACCommunication.invokeStartupUsecase(usecases[usecaseName]);
    });
  }

  public static isStartupApplied(): boolean {
    return isStartupApplied;
  }

  /*
   * Checks if the passed sysname is a known app.
   *
   * @param {string} sysname The app sysname to check if exists.
   * @return {boolean} True when app is known false otherwise.
   */
  public static hasApplication(sysname: string): boolean {
    return Object.prototype.hasOwnProperty.call(initializedApps, sysname);
  }

  public static getFocusedApplication(): FullScreenApplication {
    const focused = this.getApplications('*',
      (app) => (app instanceof FullScreenApplication) && app.isRunning() && app.isOnFocus());

    if (!focused || focused.length !== 1) {
      Logger.warn('Could not find a valid focused FullScreenApplication in Navigator::getFocusedApplication().');
    }

    return (focused || []).pop() as FullScreenApplication;
  }

  /*
   * Returns a set of apps, depending on the passed 'set' parameter.
   * When 'set' is the wildcard sting '*' all known application instances are returned.
   * When 'set' is a array of strings all apps which match with the 'sysname' are
   * returned.
   *
   * It is also passible to filter the result, by passing an filter function which
   * have to return 'false' when the app should be removed form the result.
   *
   * @param  {string | string[]} set The system names to return the apps instances.
   * @param  {function?} filter Optional filter function.
   * @return {BaseApplication | BaseApplication[]} The found applications.
   */
  public static getApplications(set: string | string[], filter?: (app: BaseApplication) => boolean): BaseApplication[] {
    const resultSet: BaseApplication[] = [];

    // If set is the wildcard '*' string convert 'set' to an array
    // with all known apps as sysname of strings
    if (set === '*') {
      set = [];

      for (const sysname in initializedApps) {
        /* istanbul ignore next else */
        if (Object.prototype.hasOwnProperty.call(initializedApps, sysname)) {
          set.push(sysname);
        }
      }
    }

    if (Array.isArray(set)) {
      set.forEach((sysname: string) => {
        const app = this.getApplication(sysname);

        if (!app) {
          Logger.warn(`Unknown application '${sysname}' in Navigator::getApplications(). Application skipped ...`);
          return;
        }

        if (typeof (filter) === 'function' && !filter(app)) {
          return;
        }

        resultSet.push(app);
      });
    }

    return resultSet;
  }

  /*
   * Returns an app instance of the passed sysname.
   *
   * @param  {string} sysname The app sysname to search.
   * @return {BaseApplication} The found app instance.
   */
  public static getApplication(sysname: string): BaseApplication {
    if (this.hasApplication(sysname)) {
      return initializedApps[sysname];
    }
    return null;
  }

  public static openApplication(sysname: string, unfocus: boolean): void {
    if (!this.hasApplication(sysname)) {
      throw new NavigatorError(`Trying to start unknown application "${sysname}".`);
    }

    const app = this.getApplication(sysname);
    const running = app.isRunning();

    if (!(app instanceof FullScreenApplication)) {
      throw new NavigatorError(`"${sysname}" is not a instance of FullScreenApplication.`);
    }

    if (!running) {
      app.open();
      IACCommunication.sendMessage(
        IACCommunication.createEvent(
          IAC_EVENT_TYPES.ONSTART,
          {navigatorUrl: `${location.protocol}//${location.host}${location.pathname}`},
        ),
        app,
      );
    }
    if (unfocus === false) {
      this.focusApplication(sysname, !running);
    }
  }

  public static reopenApplication(sysname: string, focus: boolean, urlKey?: string): void {

    if (!this.hasApplication(sysname)) {
      throw new NavigatorError(`Trying to restart unknown application "${sysname}".`);
    }

    const app = this.getApplication(sysname);
    const running = app.isRunning();

    if (running) {

      // The application is still running...
      app.unfocus();
      app.reopen(urlKey);

      IACCommunication.sendMessage(
        IACCommunication.createEvent(IAC_EVENT_TYPES.ONRESTART, { count: ++app.iacRestartCounter }),
        app,
      );
    }

    if (typeof focus === 'boolean' && focus) {
      this.focusApplication(sysname, !running);

    } else if (app.isOnFocus()) {
      // The next strange workaround
      // The mailer will not notified anymore to be onfocus, due to restarting.
      if (app.getSysname() !== 'mail') {
        IACCommunication.sendMessage(
          IACCommunication.createEvent(IAC_EVENT_TYPES.ONFOCUS, { count: ++app.iacFocusCounter }),
          app,
        );
      }
    }
  }

  public static openDialog(openerApp: BaseApplication, sysname: string): void {
    Logger.debug(`navigator.openDialog ${sysname} called from ${openerApp.getSysname()}`);
    if (!this.hasApplication(sysname)) {
      throw new NavigatorError(`Trying to restart unknown dialog application "${sysname}".`);
    }

    const dialogApp = this.getApplication(sysname);
    /*
      POS-3799: Don't open a new dialog if there is one open already UNLESS the opener app is the open dialog.
      This is so that a dialog app can still open a new dialog to replace itself.
     */
    const openDialogs = Navigator.getApplications('*', (app) => app instanceof DialogApplication &&
      app.isRunning());
    let hasDialogs = openDialogs.length > 0;
    let closeExistingDialog = false;

    // an existing dialog will be closed if
    // 1) it is the same app as the new dialog to be opened (allow an application to open a new instance of itself)
    // 2) the existing dialog is not focused (meaning it's a background dialog that never called application_ready)
    if (openDialogs.length === 1 && (openDialogs[0] === openerApp || !openDialogs[0].isOnFocus())) {
      Logger.debug(`navigator.openDialog ${sysname} closing existing dialog ${openDialogs[0].getSysname()}`);
      this.closeDialog(openDialogs[0].getSysname());
      hasDialogs = false;
      closeExistingDialog = true;
    }

    if (!dialogApp.isRunning() && !hasDialogs) {
      Logger.debug(`navigator.openDialog ${sysname} opening the dialog app`);
      // Open the dialog app
      if (closeExistingDialog) {
        setTimeout(() => {
          dialogApp.open();
        }, 100);
      } else {
        dialogApp.open();
      }

      // Inform the app to be opened
      IACCommunication.sendMessage(
        IACCommunication.createEvent(
          IAC_EVENT_TYPES.ONSTART,
          {navigatorUrl: `${location.protocol}//${location.host}${location.pathname}`},
        ),
        dialogApp,
      );

      let opener = openerApp;
      if (!opener.isOnFocus()) {
        opener = this.getFocusedApplication();
      }
      // In this case, the opener app becomes disabled

      // for background dialogs, the ONDISABLED event is dispatched only when the dialog sends an application_ready
      // event (this is done by IACEndpoint.application_ready).
      if (!dialogApp.config.start_in_background) {
        IACCommunication.sendMessage(
          IACCommunication.createEvent(IAC_EVENT_TYPES.ONDISABLED, { count: ++openerApp.iacDisabledCounter }),
          opener,
        );
      }
    }
  }

  public static closeDialog(sysname: string): void {
    if (!this.hasApplication(sysname)) {
      throw new NavigatorError(`Trying to close unknown dialog application "${sysname}".`);
    }

    const dialogApp = this.getApplication(sysname);
    const openerApp = this.getFocusedApplication();

    // inform the app to be closed
    IACCommunication.sendMessage(
      IACCommunication.createEvent(IAC_EVENT_TYPES.ONCLOSE),
      dialogApp,
    );

    // a background dialog can be running but not visible/focused. In this case, the fullscreen application never
    // received the ondisabled event so it should not receive onenabled either [SWAT-2731]
    if (dialogApp.isRunning() && dialogApp.isOnFocus()) {
      // close the dialog asynchronous with a small delay, to give the browser the chance to emit the close message to

      setTimeout(() => {
        // the opener app becomes enabled
        // TODO: don't do this if the dialog was never ready?
        IACCommunication.sendMessage(
          IACCommunication.createEvent(IAC_EVENT_TYPES.ONENABLED, { count: ++openerApp.iacEnabledCounter }),
          openerApp,
        );

      }, 100);
    }

    if (dialogApp.isRunning()) {
      dialogApp.close();
    }

  }

  private static focusApplication(sysname: string, justStarted: boolean): void {
    const app = this.getApplication(sysname);
    const visibleApp = this.getFocusedApplication();

    if (app.isOnFocus()) {
      // The application is still on focus.
      IACCommunication.sendMessage(
        IACCommunication.createEvent(IAC_EVENT_TYPES.ONRESUMEFOCUS, { count: ++app.iacResumeFocusCounter }),
        app,
      );

    } else {
      // Unfocus the current visible application
      if (visibleApp) {
        visibleApp.unfocus();

        IACCommunication.sendMessage(
          IACCommunication.createEvent(IAC_EVENT_TYPES.ONBLUR, { count: ++visibleApp.iacBlurCounter }),
          visibleApp,
        );
      }

      // Finally, we'll set the app on focus and re-brush the current theme.
      app.focus();

      // And last but not least, we have some ugly hacks...

      // The mailer will not have the onfocus event at the first start up.
      if (!(justStarted && sysname === 'mail')) {
        IACCommunication.sendMessage(
          IACCommunication.createEvent(IAC_EVENT_TYPES.ONFOCUS, { count: ++app.iacFocusCounter }),
          app,
        );
      }
    }
  }

  public static hide(): void {
    NavigatorInterface.hide();
  }

  public static show(): void {
    NavigatorInterface.show();
  }

  // TODO code duplication found, similar code found in FullScreenApplication
  public static update(onUpdated?: () => void): Promise<void> {
    return fetch(Config.get().navigator.resolve_apps_data_url, { credentials: 'same-origin' })
      .then((res) => {
        if (res.status >= 400) {
          Logger.error(`Navigator.update: Response for ${Config.get().navigator.resolve_apps_data_url} was: ${res.status} ${res.statusText}`);
          return;
        }
        Config.extend({ navigator: res.json() });

        this.getApplications('*').forEach((app) => {
          app.updateConfig();
        });

        if (typeof (onUpdated) === 'function') {
          onUpdated();
        }
      })
      .catch((reason) => {
        Logger.error(`Navigator.update: Request for ${Config.get().navigator.resolve_apps_data_url} failed`, reason);
      });
  }

  /* istanbul ignore next function */
  private static redirect(url: string, replaceHistory: boolean) {
    if (replaceHistory) {
      window.location.replace(url);

    } else {
      window.location.href = url;
    }
  }

  public static logout(target?: string): Promise<void> {
    KeepAlive.stop();

    let url = Config.get().navigator.logout_url;

    if (target) {
      url += `&target=${target}`;
    }

    // no .catch() needed since it will always resolve
    return MonitoringLoggingQueue.flush().then(() => {
      this.redirect(url, true);
    });
  }

  public static reinitialize(gotoFeature: string): Promise<void> {
    if (!this.hasApplication(IACEndpoint.SYSNAME)) {
      throw new NavigatorError('Navigator app is not configured for reinitialization.');
    }

    // App logout
    fetch(Config.get().navigator.logout_apps_url, { credentials: 'same-origin' });

    // Resolve the jump url for reinitialization
    const url = new URL(Config.get().navigator.resolve_reinit_url);
    url.searchParams.set('goto', gotoFeature);

    return fetch(url.toString(), { credentials: 'same-origin' })
      .then(async (res) => {
        /* istanbul ignore next else */
        if (res.status < 400) {
          const content = await res.json();
          this.redirect(content.url, true);
        }
      });
  }

  public static handleFeature(gotoFeature: string, mediaCode: string, data: string): Promise<void> {
    const url = new URL(Config.get().navigator.resolve_app_url);
    url.searchParams.set('feature', gotoFeature);
    url.searchParams.set('mc', mediaCode);
    url.searchParams.set('data', data);
    return fetch(url.toString(), { credentials: 'same-origin' })
      .then(async (res) => {
        if (res.status >= 400) {
          Logger.error(`Navigator.handleFeature: Response for ${url.toString()} was: ${res.status} ${res.statusText}`);
          return;
        }
        const content = await res.json();
        const app = this.getApplication(content.sysname);
        if (app && app instanceof FullScreenApplication) {
          // This is the fancy part. We'll override the app default url,
          // which is generated via goto.yml.
          app.setURL('default', content.url);

          if (app.isRunning()) {
            this.reopenApplication(content.sysname, true);
          } else {
            this.openApplication(content.sysname, false);
          }
        } else {
          Logger.warn(`No application found for feature "${gotoFeature}"`);
        }
      })
      .catch((reason) => {
        Logger.error(`Navigator.handleFeature: Request for ${url.toString()} failed`, reason);
      });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public static track(payload: any): void {
    const usecase = IACCommunication.createUsecase('tracking_track', payload);
    usecase.asRPE();
    IACHub.handle(usecase);
  }

  public static dispatchUsecase(name: string, payload?: any, asRPE = false) {
    const usecase = IACCommunication.createUsecase(name, payload)
    if (asRPE) {
      usecase.asRPE()
    }

    if (usecase.validate()) {
      IACHub.handle(usecase)
    }
  }
}
